import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

const STATUS = [{ v: "todo", l: "To do" }, { v: "in_progress", l: "In progress" }, { v: "completed", l: "Completed" }];
const PRIORITY = [{ v: "low", l: "Low" }, { v: "medium", l: "Medium" }, { v: "high", l: "High" }];

export default function TaskFormDialog({ open, onOpenChange, task, fixedClientId, fixedClientName, onSaved }) {
  const { user } = useAuth();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [clientId, setClientId] = useState("");
  const [assignees, setAssignees] = useState([]);
  const [due, setDue] = useState("");
  const [priority, setPriority] = useState("medium");
  const [status, setStatus] = useState("todo");
  const [contacts, setContacts] = useState([]);
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/messages/contacts").then((r) => setContacts(r.data)).catch(() => {});
    if (!fixedClientId) api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    if (task) {
      setTitle(task.title || ""); setDescription(task.description || "");
      setClientId(task.client_id || ""); setAssignees(task.assignee_ids || []);
      setDue(task.due_date ? task.due_date.slice(0, 10) : "");
      setPriority(task.priority || "medium"); setStatus(task.status || "todo");
    } else {
      setTitle(""); setDescription(""); setClientId(fixedClientId || "");
      setAssignees([]); setDue(""); setPriority("medium"); setStatus("todo");
    }
  }, [open, task, fixedClientId]);

  const toggleAssignee = (id) =>
    setAssignees((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const save = async () => {
    if (!title.trim()) { toast.error("Add a task title"); return; }
    setSaving(true);
    const body = {
      title, description, client_id: fixedClientId || clientId || null,
      assignee_ids: assignees, due_date: due || null, priority, status,
    };
    try {
      if (task) await api.patch(`/tasks/${task.id}`, body);
      else await api.post("/tasks", body);
      toast.success(task ? "Task updated" : "Task created");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save task");
    } finally { setSaving(false); }
  };

  const meId = user?.id;
  const people = [...(meId ? [{ id: meId, name: "Me", email: user?.email }] : []), ...contacts.filter((c) => c.id !== meId)];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-sm max-h-[90vh] overflow-y-auto" data-testid="task-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{task ? "Edit task" : "New task"}</DialogTitle>
          <DialogDescription className="sr-only">Create or edit a task with assignees, due date and priority.</DialogDescription>
        </DialogHeader>

        <label className="block text-sm font-semibold text-gray-700 mb-1">Title</label>
        <input data-testid="task-title-input" value={title} onChange={(e) => setTitle(e.target.value)}
          className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />

        <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
        <textarea data-testid="task-desc-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
          className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Due date</label>
            <input type="date" data-testid="task-due-input" value={due} onChange={(e) => setDue(e.target.value)}
              className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Priority</label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger data-testid="task-priority-trigger" className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITY.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="task-status-trigger" className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {!fixedClientId && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Client (optional)</label>
              <Select value={clientId || "none"} onValueChange={(v) => setClientId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="task-client-trigger" className="rounded-sm"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {fixedClientId && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Client</label>
              <p className="text-sm text-gray-600 px-3 py-2 border border-gray-100 rounded-sm bg-gray-50 truncate">{fixedClientName}</p>
            </div>
          )}
        </div>

        <label className="block text-sm font-semibold text-gray-700 mb-1">Assign to</label>
        <div className="border border-gray-200 rounded-sm max-h-36 overflow-y-auto p-1 mb-2" data-testid="task-assignees">
          {people.map((p) => (
            <label key={p.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded-sm cursor-pointer text-sm">
              <input type="checkbox" checked={assignees.includes(p.id)} onChange={() => toggleAssignee(p.id)}
                data-testid={`task-assignee-${p.id}`} className="accent-[#002FA7]" />
              <span className="text-gray-700">{p.name}</span>
              <span className="text-gray-400 text-xs truncate">{p.email}</span>
            </label>
          ))}
        </div>

        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="task-save-button"
            className="bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm disabled:opacity-60">
            {saving ? "Saving…" : task ? "Save changes" : "Create task"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
